import React, { useEffect, useState } from "react";
import axios from "axios";
import ADD from './assets/add.svg'
import FileUpload from "../../components/atomic/FileUpload";
import { useSelector } from "react-redux";
import { useToast } from "../../components/Toast";
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  XMarkIcon,
  CheckIcon,
  PlayIcon,
  PhotoIcon
} from "@heroicons/react/24/outline";
import constants from '../../components/constants';
import { BASE_FILEURL } from '../../components/constants';
import DeleteModal from '../../components/atomic/DeleteModal';
import Modal from '../../components/sub-components/Modal';

const API_URL = BASE_FILEURL;

const Faqs = () => {
  const [faqs, setFaqs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ question: "", answer: "", media_blob_path: "" });
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isUploading, setIsUploading] = useState(false); // <-- Add upload state
  const [openFaq, setOpenFaq] = useState(null); // Track which FAQ is open
  // Add state to track media loading
  const [mediaLoading, setMediaLoading] = useState({});
  
  const { data } = useSelector((state) => state.admin);
  const { addToast, ToastContainer } = useToast();
  const isSuperAdmin = data?.user_role === "SUPER_ADMIN";

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_URL}/faq/`);
      setFaqs(res.data);
    } catch (error) {
      addToast("Failed to fetch FAQs", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (faq) => {
    setEditing(faq.id);
    setForm({ question: faq.question, answer: faq.answer, media_blob_path: faq.media_blob_path });
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditing(null);
    setForm({ question: "", answer: "", media_blob_path: "" });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/faq/${id}`);
      addToast("FAQ erfolgreich gelöscht", "success");
      fetchFaqs();
      setDeleteConfirm(null);
    } catch (error) {
      addToast("Failed to delete FAQ", "error");
    }
  };

  const handleFileUpload = async (file) => {
    setIsUploading(true); // <-- Start loader
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (editing) formData.append("faq_id", editing);
      const res = await axios.post(`${API_URL}/faq/upload`, formData);
      setForm((prev) => ({ ...prev, media_blob_path: res.data.blob_path }));
    } finally {
      setIsUploading(false); // <-- End loader
    }
  };

  const handleSubmit = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      addToast("Question and answer are required", "error");
      return;
    }
    if (isUploading) {
      addToast("Please wait for the media upload to finish.", "error");
      return;
    }
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("question", form.question);
      formData.append("answer", form.answer);
      formData.append("media_blob_path", form.media_blob_path || "");
      if (editing) {
        await axios.put(`${API_URL}/faq/${editing}`, formData);
        addToast("FAQ updated successfully", "success");
      } else {
        await axios.post(`${API_URL}/faq/`, formData);
        addToast("FAQ erfolgreich erstellt", "success");
      }
      setModalOpen(false);
      fetchFaqs();
    } catch (error) {
      addToast("Failed to save FAQ", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm({ question: "", answer: "", media_blob_path: "" });
    setEditing(null);
  };

  const renderMedia = (faq) => {
    if (!faq.media_blob_path) return null;
    const mediaUrl = `${API_URL}/faq/media/${faq.id}`;
    const isVideo = faq.media_blob_path.match(/\.(mp4|webm)$/i);
    const isLoading = mediaLoading[faq.id];
    return (
      <div className="w-full max-w-md rounded-lg shadow-sm border border-gray-300 min-h-[180px] flex items-center justify-center relative bg-white">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white bg-opacity-60 rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-yellow"></div>
          </div>
        )}
        {isVideo ? (
          <video
            src={mediaUrl}
            controls
            className="w-full h-auto rounded-lg object-cover"
            style={{ display: isLoading ? 'none' : 'block' }}
            onLoadStart={() => setMediaLoading((prev) => ({ ...prev, [faq.id]: true }))}
            onLoadedData={() => setMediaLoading((prev) => ({ ...prev, [faq.id]: false }))}
          />
        ) : (
          <img
            src={mediaUrl}
            alt="FAQ media"
            className="w-full h-auto rounded-lg object-cover"
            style={{ display: isLoading ? 'none' : 'block' }}
            onLoad={() => setMediaLoading((prev) => ({ ...prev, [faq.id]: false }))}
            onLoadStart={() => setMediaLoading((prev) => ({ ...prev, [faq.id]: true }))}
          />
        )}
      </div>
    );
  };

  return (
    <div className="mb-16 pt-5 mt-8 mx-auto px-4 sm:px-6 rounded lg:px-8">
      <ToastContainer />
      <div className="pl-32 pr-40">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-semibold mb-6">Häufig gestellte Fragen (FAQs)</h1>
          </div>
          {isSuperAdmin && (
            <button
              onClick={handleAdd}
              className="flex items-center font-bold rounded-lg px-4 py-2 bg-custom-yellow text-white hover:bg-yellow-500 transition-colors shadow-sm"
            >
              <img src={ADD} alt="add" />
              <span className="ml-2">Neue FAQ hinzufügen</span>
            </button>
          )}
        </div>
        {/* FAQ List */}
        <div className="space-y-2">
          {isLoading && faqs.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-yellow"></div>
            </div>
          ) : faqs.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No FAQs yet</h3>
              <p className="text-gray-600">No questions have been added yet</p>
            </div>
          ) : (
            faqs.map((faq, index) => {
              const isOpen = openFaq === faq.id;
              return (
                <React.Fragment key={faq.id}>
                  <div className="px-4 py-3 flex justify-between items-center cursor-pointer bg-custom-white_light rounded-lg hover:bg-gray-50 mb-2">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="flex-shrink-0 w-8 h-8 bg-custom-yellow rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">{index + 1}</span>
                      </div>
                      <div className="flex-1 flex items-center min-h-8">
                        <h3 className="text-sm font-medium text-gray-900">{faq.question}</h3>
                      </div>
                    </div>
                    <button
                      className={`transition-transform duration-300 ml-4 mt-1 focus:outline-none group-hover:scale-110 ${isOpen ? 'rotate-90' : 'rotate-0'}`}
                      aria-label={isOpen ? 'Collapse FAQ' : 'Expand FAQ'}
                      tabIndex={0}
                      onClick={e => { e.stopPropagation(); setOpenFaq(isOpen ? null : faq.id); }}
                    >
                      <img src={constants.arrow} alt="arrow" className="w-6 h-6" />
                    </button>
                    {isSuperAdmin && (
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={e => { e.stopPropagation(); handleEdit(faq); }}
                          className="p-2 text-gray-400 hover:text-custom-yellow hover:bg-custom-yellow/10 rounded-lg transition-colors duration-200"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); setDeleteConfirm(faq.id); }}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {isOpen && (
                    <div className="px-8 pb-6 -mt-2 mb-2 text-left w-full">
                      <div className="text-sm font-medium text-gray-900 mb-2 whitespace-pre-line">{faq.answer}</div>
                      {faq.media_blob_path && (
                        <div className="mt-2">{renderMedia(faq)}</div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onDelete={() => handleDelete(deleteConfirm)}
        title="FAQ löschen"
        message="Sind Sie sicher, dass Sie diese FAQ löschen möchten?"
      />

      {/* Add/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        onYes={handleSubmit}
        onNo={closeModal}
        title={editing ? "FAQ bearbeiten" : "Neue FAQ hinzufügen"}
        confirmText={editing ? "Update hinzufügen" : "FAQ hinzufügen"}
        cancelText="Abbrechen"
        width="max-w-4xl"
      >
        <div className="space-y-6">
          {/* Question Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Frage *
            </label>
            <input
              type="text"
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              placeholder="Geben Sie die häufig gestellte Frage ein..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-yellow focus:border-transparent transition-colors duration-200 text-left"
              required
            />
          </div>

          {/* Answer Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
             Antwort *
            </label>
            <textarea
              value={form.answer}
              onChange={(e) => setForm({ ...form, answer: e.target.value })}
              placeholder="Geben Sie eine ausführliche Antwort ein..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-yellow focus:border-transparent transition-colors duration-200 resize-none text-left"
              required
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Medien (optional)
            </label>
            <FileUpload 
              onFileUpload={(files) => handleFileUpload(files[0])} 
              accept="image/*,video/*"
            />
            <p className="text-sm text-gray-500 mt-1">
              Laden Sie ein Bild oder ein Video hoch, um Ihre Antwort zu unterstützen
            </p>
          </div>

          {/* Media Preview */}
          {form.media_blob_path && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Medienvorschau
              </label>
              {renderMedia(form.media_blob_path)}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Faqs;
